// VRG-744 — react-query hooks for Competitor research (`/internal/competitors`).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { competitorResearchApi } from 'services'
import type { CellInput, PriceInput } from 'services'

const FIVE_MINUTES = 5 * 60 * 1000
const KEY = ['internal', 'competitor-research'] as const

export const useCompetitorPricing = () =>
  useQuery({ queryKey: [...KEY, 'pricing'], queryFn: () => competitorResearchApi.pricing(), staleTime: FIVE_MINUTES, retry: false })

export const useCompetitorFeatures = () =>
  useQuery({ queryKey: [...KEY, 'features'], queryFn: () => competitorResearchApi.features(), staleTime: FIVE_MINUTES, retry: false })

export const useCompetitorContent = (enabled = true) =>
  useQuery({
    queryKey: [...KEY, 'content'],
    queryFn: () => competitorResearchApi.content(),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled,
  })

export const useCompetitorSeo = (market: string) =>
  useQuery({ queryKey: [...KEY, 'seo', market], queryFn: () => competitorResearchApi.seo(market), staleTime: FIVE_MINUTES, retry: false })

const useResearchMutation = <A, R>(fn: (args: A) => Promise<R>) => {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: KEY }) })
}

export const useAddCompetitorPrice = () =>
  useResearchMutation(({ domain, input }: { domain: string; input: PriceInput }) => competitorResearchApi.addPrice(domain, input))

export const useRetireCompetitorPrice = () => useResearchMutation((id: string) => competitorResearchApi.retirePrice(id))

export const useCreateCompetitorFeature = () =>
  useResearchMutation((input: { name: string; category: string; description: string | null }) =>
    competitorResearchApi.createFeature(input),
  )

export const usePatchCompetitorFeature = () =>
  useResearchMutation(({ id, patch }: { id: string; patch: Parameters<typeof competitorResearchApi.patchFeature>[1] }) =>
    competitorResearchApi.patchFeature(id, patch),
  )

export const useSetFeatureCell = () =>
  useResearchMutation(({ featureId, domain, input }: { featureId: string; domain: string; input: CellInput }) =>
    competitorResearchApi.setCell(featureId, domain, input),
  )
